
























import Vue from 'vue'
import { Component, Prop, Emit, Watch } from 'vue-property-decorator'
import { showErrors, showNotification } from '@/utils/snack'
import { SnackType } from '@/interfaces/snack'
import AppDialog from '@/components/AppDialog.vue'
import AppSteps from '@/components/AppSteps.vue'
import AccountAssetFormInfo from '@/modules/AccountAsset/components/forms/AccountAssetFormInfo.vue'
import AccountAssetFormVehicle from '@/modules/AccountAsset/components/forms/AccountAssetFormVehicle.vue'
import AccountAssetFormType from '@/modules/AccountAsset/components/forms/AccountAssetFormType.vue'

import { newAssetVehicle, editAssetVehicle } from '@/modules/AccountAsset/gql/account-asset.gql'
import { AccountAsset } from '@/modules/AccountAsset/interfaces/account-asset'
import { IAssetInfoInput, IAccountVehicleInput } from '@/modules/AccountAsset/interfaces/account-asset-form'
import { VehicleOwnership } from '@/modules/Vehicle/interfaces/vehicle'
import { vehicleOwnershipOptions } from '@/modules/Vehicle/helpers/vehicle-ownership-options'
import { vehicleUseageOptions } from '@/modules/Vehicle/helpers/vehicle-useage-options'
@Component({
  components: {
    AccountAssetFormInfo,
    AccountAssetFormVehicle,
    AccountAssetFormType,
    AppDialog,
    AppSteps
  }
})
export default class AssetVehicleForm extends Vue {
  @Prop(Boolean) value!: boolean
  @Prop(Number) readonly step!: number
  @Prop(String) readonly title!: string
  @Prop({ type: Object as () => AccountAsset }) readonly accountAsset!: AccountAsset
  @Prop(Number) readonly accountId!: number

  showDialog = false

  loading = false

  stepIndex = 0

  infoInput: IAssetInfoInput = {
    name: '',
    notes: ''
  }

  vehicleInput: IAccountVehicleInput = {
    vid: '',
    owner: '',
    brand: '',
    color: '',
    type: '',
    price: '',
    ownershipIdx: -1,
    useageIdx: -1,
    mileage: ''
  }

  get steps () {
    return [
      ['Asset Info', 770, 421],
      ['Vehicle Info', 770, 451]
    ]
  }

  get stepItems () {
    return this.steps.map(st => st[0])
  }

  get showBackBtn () {
    return this.stepIndex > 0
  }

  get confirmText () {
    return this.stepIndex < this.steps.length - 1
      ? 'Next'
      : this.accountAsset
        ? 'Save'
        : 'Create'
  }

  get confirmAheadText () {
    return this.accountAsset ? 'Save' : 'Create'
  }

  get canPost () {
    return this.stepIndex === this.steps.length - 1
  }

  @Watch('value')
  onValueChanged (val: boolean) {
    if (val) {
      this.resetStep()
      this.initInput()
    }
    this.showDialog = val
  }

  resetStep () {
    this.stepIndex = this.step
  }

  initInput () {
    if (this.accountAsset) {
      const {
        name, notes, vehicle
      } = this.accountAsset

      this.infoInput = {
        name,
        notes
      }
      if (!vehicle) {
        return
      }

      const { vid, owner, brand, color, type, price, ownership, useage, mileage } = vehicle
      const ownershipIdx = vehicleOwnershipOptions.findIndex(item => item.value === ownership)
      const useageIdx = vehicleUseageOptions.findIndex(primaryItem => primaryItem.value === useage)

      this.vehicleInput = {
        vid, owner, brand, color, type, price, ownershipIdx, useageIdx, mileage
      }
    } else {
      this.clearForm()
    }
  }

  handleStepClick (index: number) {
    if (!this.accountAsset) {
      return
    }

    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    this.stepIndex = index
  }

  handleValidate () {
    const { stepIndex, steps } = this
    const refName = steps[stepIndex][0] as string
    return (this.$refs[refName] as Vue & {
      validate: () => null | { [key: string]: number | string }
    }).validate()
  }

  handleConfirm (event: Event, finishAhead = false) {
    const pass = this.handleValidate()
    if (!pass) {
      return
    }

    if (this.stepIndex === this.steps.length - 1 || finishAhead) {
      this.loading = true
      if (this.accountAsset) {
        this.handleUpdate()
      } else {
        this.handleCreate()
      }
    } else {
      this.stepIndex += 1
    }
  }

  handleBack () {
    if (this.stepIndex > 0) {
      this.stepIndex -= 1
    } else {
      this.showDialog = false
    }
  }

  handleCreate () {
    return this.handleCreateVehicle()
  }

  handleUpdate () {
    return this.handleUpdateVehicle()
  }

  async handleCreateVehicle () {
    const {
      infoInput: {
        name,
        notes
      },
      vehicleInput: {
        vid, owner, brand, color, type, price, ownershipIdx, useageIdx, mileage
      }
    } = this

    try {
      const res = await this.$apollo.mutate({
        mutation: newAssetVehicle,
        variables: {
          input: {
            vid,
            owner,
            brand,
            color,
            type,
            price,
            name,
            notes,
            ownership: vehicleOwnershipOptions[ownershipIdx].value,
            useage: vehicleUseageOptions[useageIdx].value,
            mileage
          },
          accountId: this.accountId
        }
      })
      this.clearForm()
      this.onSuccess('Create the new vehicle successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  async handleUpdateVehicle () {
    const {
      infoInput: {
        name,
        notes
      },
      vehicleInput: {
        vid,
        owner,
        brand,
        color,
        type,
        price,
        ownershipIdx,
        useageIdx,
        mileage
      }
    } = this

    try {
      const res = await this.$apollo.mutate({
        mutation: editAssetVehicle,
        variables: {
          input: {
            name,
            notes,
            vid,
            owner,
            brand,
            color,
            type,
            price,
            ownership: vehicleOwnershipOptions[ownershipIdx].value,
            useage: vehicleUseageOptions[useageIdx].value,
            mileage
          },
          accountAssetId: +this.accountAsset.id
        }
      })
      this.clearForm()
      this.onSuccess('Update vehicle successfully')
    } catch (e) {
      this.loading = false
      showErrors(e.graphQLErrors)
    }
  }

  clearForm () {
    this.loading = false
    this.infoInput = {
      name: '',
      notes: ''
    }
    this.vehicleInput = {
      vid: '',
      owner: '',
      brand: '',
      color: '',
      type: '',
      price: '',
      ownershipIdx: -1,
      useageIdx: -1,
      mileage: ''
    }
  }

  @Emit('cancel')
  handleCancel () {
    this.$emit('input', false)
    this.clearForm()
  }

  @Emit('success')
  onSuccess (msg = '') {
    this.$emit('input', false)
    this.resetStep()
    if (msg) {
      showNotification({
        msg,
        type: SnackType.Success
      })
    }
  }
}
